<template>
    <div class="condition-header">
        <ConditionSelector
            v-if="hierarchyData"
            :component-selectors="componentSelectors"
            :is-pending="hierarchyIsPending"
            :is-error="isErrorHierarchy"
            :bookmark-id="bookmarkId"
            :component-type="ComponentType.Transformer"
            @update-value="onValueUpdate"
            @reset-values="onResetValues"
        />
        <ConditionStatusHeaderActions
            v-if="transformerId && isContentDisplayed"
            :key="transformerId"
            :component-id="transformerId"
            :general-status="transformerData?.generalStatus"
            :last-synced-date="transformerData?.lastSyncedDate"
            :is-pending="transformerIsPending"
            :is-error="isErrorTransformer"
            :component-type="ComponentType.Transformer"
            :is-no-action-set="transformerData?.isNoActionSet"
            :no-action-component="transformerData?.noActionComponent"
            @export-button-click="onExportButtonClick"
        />
    </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import { flattenTransformersHierarchy, useTransformersHierarchyQuery } from '@/api/queries/transformers/useTransformersHierarchyQuery'
import { ComponentSelectorConfiguration, plant, plantGroup, transformer } from '@/configuration/component/component-selector/component-selector-configuration'
import { ComponentType } from '@/configuration/component/component-types'
import ConditionSelector from '@/components/component-selector/ComponentSelector.vue'
import { computed, ref, watch } from 'vue'
import { SelectOptions } from '@/components/form-inputs/common/select-option'
import { useExportStateStore } from '@/store/exportStateStore'
import { useComponentStatusQuery } from '@/api/queries/general/useComponentStatusQuery'
import ConditionStatusHeaderActions from '@/components/condition-status-header-actions/ConditionStatusHeaderActions.vue'
import { toRef } from 'vue'

const props = defineProps<{
    transformerId?: string
    isContentDisplayed: boolean
}>()

const router = useRouter()
const exportStateStore = useExportStateStore()
const transformerIdRef = toRef(() => props.transformerId)

const { data: hierarchyData, isPending: hierarchyIsPending, isError: isErrorHierarchy } = useTransformersHierarchyQuery()
const { data: transformerData, isPending: transformerIsPending, isError: isErrorTransformer } = useComponentStatusQuery(transformerIdRef, ComponentType.Transformer)

const transformerList = computed(() => {
    if (!hierarchyData.value?.plantGroups) {
        return []
    }

    return flattenTransformersHierarchy(hierarchyData.value.plantGroups)
})

const bookmarkId = computed(
    () => transformerList.value.find((e) => e.transformer.id === props.transformerId)?.transformer.bookmarkId,
)

const getComponentSelectors = () => {
    const componentStructure = ComponentSelectorConfiguration[ComponentType.Transformer]
    const selectedTransformer = props.transformerId ? transformerList.value.find((e) => e.transformer.id === props.transformerId) : undefined

    if (props.transformerId && !selectedTransformer) {
        router.replace({
            name: 'Transformers_ConditionStatus',
            params: {
                transformerId: undefined,
            },
        })
    }

    let plantGroupSid: number | undefined = undefined
    let plantSid: number | undefined = undefined

    if (selectedTransformer) {
        plantGroupSid = selectedTransformer.plantGroup.sid
        plantSid = selectedTransformer.plant.sid
    }

    return componentStructure.map((selector) => {
        let options: SelectOptions<string | number, { isBookmarked?: boolean }> = []
        let modelValue = undefined
        let disabled = false

        if (selector.name === plantGroup) {
            modelValue = plantGroupSid
            options = getOptionsForDropdown(selector.name)
        } else if (selector.name === plant) {
            modelValue = plantSid
            options = getOptionsForDropdown(selector.name, plantGroupSid?.toString())
            disabled = !plantGroupSid
        } else if (selector.name === transformer) {
            modelValue = selectedTransformer?.transformer.id
            options = getOptionsForDropdown(selector.name, plantSid?.toString())
            disabled = !plantSid
        }

        return {
            ...selector,
            options,
            isLastChild: selector.name === transformer,
            modelValue,
            disabled,
        }
    })
}

const getOptionsForDropdown = (dropdownName: string, parentValue?: string) => {
    if (dropdownName === plantGroup) {
        return hierarchyData.value?.plantGroups?.map((e) => ({
            label: e.name ?? '-',
            value: e.sid ?? 0,
        })) ?? []
    } else if (dropdownName === plant) {
        if (!parentValue) {
            return []
        }
        const selectedPlantGroup = hierarchyData.value?.plantGroups?.find((e) => e.sid?.toString() === parentValue)
        if (!selectedPlantGroup?.plants) {
            return []
        }
        return selectedPlantGroup.plants.map((e) => ({
            label: e.name ?? '-',
            value: e.sid ?? 0,
        }))
    } else if (dropdownName === transformer) {
        if (!parentValue) {
            return []
        }
        const selectedPlant = hierarchyData.value?.plantGroups?.flatMap((e) => e.plants ?? [])?.find((e) => e.sid?.toString() === parentValue)
        if (!selectedPlant?.transformers) {
            return []
        }
        const plantList = selectedPlant.transformers.map((e) => ({
            label:
                !e.functionalLocation || e.transformerSid === e.functionalLocation
                    ? `${e.transformerSid} / - / ${e.transformerName}`
                    : `${e.transformerSid} / ${e.functionalLocation} / ${e.transformerName}`,
            value: e.transformerSid ?? '',
            extraData: {
                isBookmarked: !!e.bookmarkId,
            },
        }))
        return plantList.toSorted((a) => {
            return a.extraData.isBookmarked ? -1 : 1
        })
    } else {
        return []
    }
}

let componentSelectors = ref(hierarchyData.value ? getComponentSelectors() : [] )

watch(
    [() => hierarchyData.value],
    ([hierarchyData]) => {
        if (!hierarchyData) {
            return
        }
        componentSelectors.value = getComponentSelectors()
    },
    { immediate: true },
)

const onValueUpdate = (payload: { [key: string]: string | number | undefined }) => {
    let updatedValueIndex: null | number = null
    componentSelectors.value = componentSelectors.value.map((selector, index) => {
        if (selector.name in payload) {
            updatedValueIndex = index
            return {
                ...selector,
                modelValue: payload[selector.name],
            }
        } else if (updatedValueIndex !== null && index === updatedValueIndex + 1) {
            const updatedValue = Object.values(payload)[0]?.toString()
            return {
                ...selector,
                disabled: !updatedValue,
                options: getOptionsForDropdown(selector.name, updatedValue)
            }
        }
        return selector
    })
    if (payload[transformer]) {
        router.replace({
            name: 'Transformers_ConditionStatus',
            params: {
                transformerId: payload[transformer],
            },
        })
    }
}

const onResetValues = () => {
    componentSelectors.value = componentSelectors.value.map((selector) => {
        return {
            ...selector,
            modelValue: undefined,
            disabled: selector.name !== plantGroup,
            options: selector.name === plantGroup ? getOptionsForDropdown(selector.name) : []
        }
    })
    router.replace({
        name: 'Transformers_ConditionStatus',
        params: {
            transformerId: undefined,
        },
    })
}

const onExportButtonClick = () => {
    router.push({
        name: 'Export_ConditionStatus',
        params: {
            transformerId: props.transformerId,
        },
        query: exportStateStore.conditionStatusExportParameters,
    })
}
</script>

<style scoped lang="scss">
.condition-header {
    @include spacing-page;

    display: flex;
    flex-direction: column;
    gap: $spacing-m;

    background-color: $pbl-foreground;
    border-bottom: 1px solid $pbl-border-muted;

    &__title {
        font-size: 1.25rem;
        font-weight: 500;
    }
}
</style>
